import { findNormalizedRow, makeChartData } from '../../shared/data/charting';
import {
  EVAL_AREAS,
  SURVEY_FIELDS,
  assembleGroupCodes,
  collapseGroupDa,
  resolveClientSex,
  scoreSaFromFields,
} from '../../view_web_eval/report/reportCodes';

/**
 * Legacy chart compare inputs are the RTDB selectedCase + master that
 * EvalGraphs already feeds to makeChartData (Raw + Normalized tabs).
 * Do not route this through cloud adapters or Firestore sd.
 * Do not use scoreDaFromNormalizedRow (hardcoded 1 ± error).
 */
const LEGACY_SA_ALIASES = {
  FA: [...SURVEY_FIELDS.FA, 'Adult Female', 'Female Adult', 'Female Adults'],
  MA: [...SURVEY_FIELDS.MA, 'Adult Male', 'Male Adult', 'Male Adults'],
  FAD: [...SURVEY_FIELDS.FAD, 'Teen Female', 'Female Adolescent', 'Female Teenagers'],
  MAD: [...SURVEY_FIELDS.MAD, 'Teen Male', 'Male Adolescent', 'Male Teenagers'],
  FC: [...SURVEY_FIELDS.FC, 'Child Female', 'Female Child', 'Female Children'],
  MC: [...SURVEY_FIELDS.MC, 'Child Male', 'Male Child', 'Male Children'],
};

export function hasLegacyChartInputs(selectedCase, master) {
  const clientImage = selectedCase?.Stim?.UVF?.T5_F?.Image;
  const masterImage = master?.Stim?.UVF?.T5_F?.Image;
  return Number.isFinite(Number(clientImage))
    && Number.isFinite(Number(masterImage?.AVG))
    && Number.isFinite(Number(masterImage?.STD_DEV));
}

/**
 * The values EvalGraphsDisplay actually plots:
 *   Image (or image) vs ctrl-Image ± ctrl-Image-Error
 * Raw tab: client UVF vs AVG ± (AVG * STD_DEV)
 * Normalized tab: client/AVG vs 1 ± STD_DEV
 * web_eval leftover charts use lowercase keys.
 */
export function scoreDaFromLegacyChartRow(row) {
  if (!row) return null;
  const client = Number(row.Image ?? row.image);
  const control = Number(row['ctrl-Image'] ?? row['ctrl-image']);
  const error = Number(row['ctrl-Image-Error'] ?? row['ctrl-image-Error']);
  if (!Number.isFinite(client) || !Number.isFinite(control) || !Number.isFinite(error)) {
    return null;
  }
  if (client > control + error) return 3;
  if (client < control - error) return 1;
  return 2;
}

export function scoreLegacyGroupDaFromChart(rows, group) {
  return collapseGroupDa(
    group.tanners.map((tannerKey) => (
      scoreDaFromLegacyChartRow(findNormalizedRow(rows, tannerKey))
    ))
  );
}

export function chartLegacyPlotRows({
  selectedCase,
  master,
  rawData,
  normalizedData,
} = {}) {
  const hasRaw = Array.isArray(rawData);
  const hasNormalized = Array.isArray(normalizedData);
  if (hasRaw || hasNormalized) {
    return {
      raw: hasRaw ? rawData : null,
      normalized: hasNormalized ? normalizedData : null,
    };
  }
  if (!hasLegacyChartInputs(selectedCase, master)) {
    return { raw: null, normalized: null };
  }
  try {
    const [raw, normalized] = makeChartData({ selectedCase, master });
    return { raw, normalized };
  } catch (error) {
    console.error('Failed to build legacy chart rows for DA:', error);
    return { raw: null, normalized: null };
  }
}

function flattenLegacySurvey(node, into = {}) {
  if (!node || typeof node !== 'object') return into;
  if (Array.isArray(node)) {
    node.forEach((item) => flattenLegacySurvey(item, into));
    return into;
  }
  if (node.answers && typeof node.answers === 'object') {
    Object.assign(into, node.answers);
  }
  Object.entries(node).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    if (typeof value !== 'object') {
      into[key] = value;
      return;
    }
    if (key !== 'answers') flattenLegacySurvey(value, into);
  });
  return into;
}

export function scoreLegacyGroupSa(selectedCase, groupId) {
  const survey = selectedCase?.post_test_survey
    ?? selectedCase?.post_eval_survey
    ?? selectedCase?.eval_data?.post_test_survey;
  const flat = {
    ...flattenLegacySurvey(survey),
    ...flattenLegacySurvey(selectedCase?.eval_data),
  };
  return scoreSaFromFields(flat, LEGACY_SA_ALIASES[groupId] ?? []);
}

export function resolveLegacyClient(selectedCase) {
  const client = selectedCase?.eval_data?.client ?? {};
  return {
    id: client.id ?? selectedCase?.cus_id ?? selectedCase?.eval_id ?? null,
    Name: client.name ?? client.Name ?? null,
    name: client.name ?? client.Name ?? null,
    Sex: client.Sex ?? client.sex,
    organization_id: selectedCase?.cus_id ?? null,
  };
}

export function resolveLegacyCaseNumber(selectedCase) {
  return selectedCase?.eval_data?.case_num
    ?? selectedCase?.eval_data?.case_number
    ?? selectedCase?.case_num
    ?? null;
}

/**
 * Legacy scorer. DA is Image vs the plotted control bar ± error bar from
 * the same makeChartData rows EvalGraphs shows (Normalized tab by default;
 * Raw uses AVG ± AVG*STD_DEV). Not cloud Firestore sd. Not 1 ± sd.
 */
export function scoreLegacyEvalCodes({
  selectedCase,
  master,
  rawData,
  normalizedData,
  overrides = {},
} = {}) {
  const plots = chartLegacyPlotRows({ selectedCase, master, rawData, normalizedData });
  // Default UI tab is Normalized; Raw uses the same compare on absolute UVF.
  const rows = plots.normalized ?? plots.raw;
  const chartPathReady = hasLegacyChartInputs(selectedCase, master) && Array.isArray(rows);
  const client = resolveLegacyClient(selectedCase);

  return {
    sex: resolveClientSex(client) ?? resolveClientSex(overrides.client) ?? null,
    caution: {
      cs: overrides.cs ?? null,
      stub: overrides.cs == null,
    },
    groups: assembleGroupCodes({
      rows,
      saForGroup: (groupId) => scoreLegacyGroupSa(selectedCase, groupId),
      scoreDaFromRow: scoreDaFromLegacyChartRow,
      overrides,
    }),
    chartPathReady,
    selectedCase,
    master,
    area: EVAL_AREAS.LEGACY,
    plotSource: plots.normalized ? 'normalized' : (plots.raw ? 'raw' : null),
  };
}

